import { useEffect, useState } from 'react'
import { PlusCircleFilled, EditOutlined, DeleteOutlined, UserOutlined, HolderOutlined } from '@ant-design/icons'
import { router } from '@inertiajs/react'
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import { SortableContext, useSortable, rectSortingStrategy, horizontalListSortingStrategy, arrayMove } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import axios from '@/lib/axios'
import { ZeroState } from '@/components/patterns'
import { Button, IconButton, ConfirmModal, TableSearch, message, Select } from '@/components/ui'
import SpeakerDrawer from './SpeakerDrawer'

type SpeakerRole = 'speaker' | 'moderator' | 'chairperson'

/** Singular/plural display labels for each speaker role, used for buttons and zero-states. */
const ROLE_LABELS: Record<SpeakerRole, { singular: string; plural: string }> = {
  speaker: { singular: 'Speaker', plural: 'Speakers' },
  moderator: { singular: 'Moderator', plural: 'Moderators' },
  chairperson: { singular: 'Chairperson', plural: 'Chairpersons' }
}

interface Event {
  id: number
  name: string
  slug: string
}

interface Speaker {
  id: number
  full_name: string
  email: string | null
  job_title: string | null
  organization: string | null
  phone_number: string | null
  short_bio: string | null
  visible: boolean
  photo_url: string | null
  role: SpeakerRole
  position: number
}

interface SpeakersTabProps {
  event: Event
  speakers: Speaker[]
  /** Persisted per-event ordering of the role sub-tabs. Drives the sortable tab strip. */
  speakerRoleOrder: SpeakerRole[]
}

interface SpeakerCardProps {
  speaker: Speaker
  onEdit: (speaker: Speaker) => void
  onDelete: (speakerId: number, speakerName: string) => void
  /** Optional drag handle rendered inline at the start of the row when reordering is enabled. */
  dragHandle?: React.ReactNode
}

/**
 * Compact horizontal speaker card: small avatar on the left, details + actions on the right.
 * The row-style layout stays compact in width while letting text wrap to as many lines as
 * needed, so the card grows vertically to fit the full name / job title / organization.
 */
function SpeakerCard({ speaker, onEdit, onDelete, dragHandle }: SpeakerCardProps) {
  return (
    <div className="relative flex items-start gap-3 bg-white border border-gray-200 rounded-md p-2.5 hover:shadow-md transition-shadow">
      {dragHandle}
      <div className="shrink-0 h-14 w-14 rounded-md bg-gray-100 flex items-center justify-center overflow-hidden">
        {speaker.photo_url ? (
          <img
            src={speaker.photo_url}
            alt={speaker.full_name}
            className="h-full w-full object-cover"
          />
        ) : (
          <UserOutlined className="text-2xl text-gray-400" />
        )}
      </div>
      <div className="flex-1">
        <div className="flex items-start gap-1.5">
          <h3 className="font-semibold text-sm text-gray-900 break-words whitespace-normal">{speaker.full_name}</h3>
          {!speaker.visible && (
            <span className="shrink-0 mt-0.5 text-[10px] leading-none bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
              Hidden
            </span>
          )}
        </div>
        {speaker.job_title && (
          <p className="text-xs text-gray-600 break-words whitespace-normal">{speaker.job_title}</p>
        )}
        {speaker.organization && (
          <p className="text-xs text-gray-500 break-words whitespace-normal">{speaker.organization}</p>
        )}
      </div>
      <div className="shrink-0 flex items-center gap-0.5">
        <IconButton
          icon={<EditOutlined />}
          onClick={() => onEdit(speaker)}
          ariaLabel="Edit speaker"
          title="Edit"
        />
        <IconButton
          danger
          icon={<DeleteOutlined />}
          onClick={() => onDelete(speaker.id, speaker.full_name)}
          ariaLabel="Delete speaker"
          title="Delete"
        />
      </div>
    </div>
  )
}

interface SortableSpeakerCardProps {
  speaker: Speaker
  onEdit: (speaker: Speaker) => void
  onDelete: (speakerId: number, speakerName: string) => void
}

/** Wraps a SpeakerCard with @dnd-kit sortable behaviour and a dedicated drag handle. */
function SortableSpeakerCard({ speaker, onEdit, onDelete }: SortableSpeakerCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: speaker.id })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    ...(isDragging ? { zIndex: 5 } : {}),
  }

  const dragHandle = (
    <button
      type="button"
      ref={setActivatorNodeRef}
      className="shrink-0 -ml-1 px-1 py-2 text-gray-400 hover:text-gray-700 cursor-move touch-none self-stretch flex items-center"
      aria-label="Drag to reorder"
      {...attributes}
      {...listeners}
    >
      <HolderOutlined />
    </button>
  )

  return (
    <div ref={setNodeRef} style={style}>
      <SpeakerCard speaker={speaker} onEdit={onEdit} onDelete={onDelete} dragHandle={dragHandle} />
    </div>
  )
}

interface SortableRoleChipProps {
  role: SpeakerRole
  label: string
  active: boolean
  onSelect: () => void
}

/**
 * A role sub-tab rendered as a chip that is both clickable and draggable.
 * The shared PointerSensor's 4px activation distance lets a click select the role while
 * a real drag reorders it. Styled to mirror the antd Segmented control it replaces.
 */
function SortableRoleChip({ role, label, active, onSelect }: SortableRoleChipProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: role })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    ...(isDragging ? { zIndex: 5 } : {}),
  }

  const baseClass = 'inline-flex items-center gap-1.5 px-3 py-1 text-sm rounded select-none touch-none transition-colors'
  const cursorClass = isDragging ? 'cursor-grabbing' : 'cursor-grab'
  const stateClass = active
    ? 'bg-white text-gray-900 shadow-sm font-medium'
    : 'text-gray-600 hover:text-gray-900'

  return (
    <button
      type="button"
      ref={setNodeRef}
      style={style}
      onClick={onSelect}
      className={`${baseClass} ${cursorClass} ${stateClass}`}
      {...attributes}
      {...listeners}
    >
      {/* Grip icon signals the chip is draggable; muted + smaller than the label to stay subtle. */}
      <HolderOutlined className="text-xs text-gray-400" />
      {label}
    </button>
  )
}

export default function SpeakersTab({ event, speakers, speakerRoleOrder }: SpeakersTabProps) {
  const [speakerList, setSpeakerList] = useState<Speaker[]>(speakers)
  const [roleOrder, setRoleOrder] = useState<SpeakerRole[]>(() => speakerRoleOrder)
  const [activeRole, setActiveRole] = useState<SpeakerRole>(() => speakerRoleOrder[0] ?? 'speaker')
  const [searchTerm, setSearchTerm] = useState('')
  const [appliedSearch, setAppliedSearch] = useState('')
  const [visibilityFilter, setVisibilityFilter] = useState<string>('all')
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [editingSpeaker, setEditingSpeaker] = useState<Speaker | null>(null)
  const [deleteModal, setDeleteModal] = useState<{ visible: boolean; speakerId: number | null; speakerName: string }>({
    visible: false,
    speakerId: null,
    speakerName: ''
  })

  // Resync local state when the speakers prop changes (e.g. after router.reload on delete).
  useEffect(() => {
    setSpeakerList(speakers)
  }, [speakers])

  // Resync the role-tab order when the persisted prop changes (e.g. after router.reload).
  useEffect(() => {
    setRoleOrder(speakerRoleOrder)
  }, [speakerRoleOrder])

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))

  const handleCreateClick = () => {
    setEditingSpeaker(null)
    setDrawerVisible(true)
  }

  const handleEditClick = (speaker: Speaker) => {
    setEditingSpeaker(speaker)
    setDrawerVisible(true)
  }

  const handleDeleteClick = (speakerId: number, speakerName: string) => {
    setDeleteModal({ visible: true, speakerId, speakerName })
  }

  const handleDelete = async () => {
    if (!deleteModal.speakerId) return
    try {
      const response = await axios.delete(`/events/${event.slug}/agenda/speakers/${deleteModal.speakerId}`)

      if (response.data.success) {
        message.success(response.data.message)
        router.reload()
      }
    } catch (error: any) {
      message.error(error.response?.data?.errors?.join(', ') || 'Failed to delete speaker')
    } finally {
      setDeleteModal({ visible: false, speakerId: null, speakerName: '' })
    }
  }

  const handleSearch = () => {
    setAppliedSearch(searchTerm)
  }

  const handleSearchReset = () => {
    setSearchTerm('')
    setAppliedSearch('')
  }

  const handleDragEnd = async (dragEvent: DragEndEvent) => {
    const { active, over } = dragEvent
    if (!over || active.id === over.id) return

    const activeSpeaker = speakerList.find(s => s.id === active.id)
    const overSpeaker = speakerList.find(s => s.id === over.id)
    if (!activeSpeaker || !overSpeaker) return

    // Compute indices against the FULL list (drag is only enabled when unfiltered).
    const oldIndex = speakerList.findIndex(s => s.id === active.id)
    const newIndex = speakerList.findIndex(s => s.id === over.id)
    const previous = speakerList

    // Optimistic reorder of the full list.
    setSpeakerList(arrayMove(speakerList, oldIndex, newIndex))

    try {
      const res = await axios.patch(
        `/events/${event.slug}/agenda/speakers/${active.id}/reorder`,
        { position: overSpeaker.position }
      )
      if (res.data.success) {
        setSpeakerList(res.data.speakers)
      } else {
        setSpeakerList(previous)
        message.error(res.data.errors?.join(', ') || 'Failed to reorder')
      }
    } catch (e: any) {
      setSpeakerList(previous)
      message.error(e.response?.data?.errors?.join(', ') || 'Failed to reorder speaker')
    }
  }

  // Reorder the role sub-tabs. Always enabled (independent of the speaker search/visibility
  // filters): optimistically apply the new order, persist it, and revert on failure.
  const handleRoleDragEnd = async (dragEvent: DragEndEvent) => {
    const { active, over } = dragEvent
    if (!over || active.id === over.id) return

    const oldIndex = roleOrder.findIndex(r => r === active.id)
    const newIndex = roleOrder.findIndex(r => r === over.id)
    if (oldIndex === -1 || newIndex === -1) return

    const previous = roleOrder
    const newOrder = arrayMove(roleOrder, oldIndex, newIndex)
    setRoleOrder(newOrder)

    try {
      const res = await axios.patch(
        `/events/${event.slug}/agenda/role-order`,
        { role_order: newOrder }
      )
      if (res.data.success) {
        setRoleOrder(res.data.speaker_role_order)
      } else {
        setRoleOrder(previous)
        message.error(res.data.errors?.join(', ') || 'Failed to reorder roles')
      }
    } catch (e: any) {
      setRoleOrder(previous)
      message.error(e.response?.data?.errors?.join(', ') || 'Failed to reorder roles')
    }
  }

  const filteredSpeakers = speakerList.filter(speaker => {
    const matchesRole = speaker.role === activeRole
    const matchesSearch = speaker.full_name.toLowerCase().includes(appliedSearch.toLowerCase())
    const matchesVisibility =
      visibilityFilter === 'all' ||
      (visibilityFilter === 'visible' && speaker.visible) ||
      (visibilityFilter === 'hidden' && !speaker.visible)
    return matchesRole && matchesSearch && matchesVisibility
  })

  // Only allow drag-to-reorder when no filter is active, so positions map cleanly to the full list.
  const isReorderEnabled = appliedSearch === '' && visibilityFilter === 'all'

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <TableSearch
            value={searchTerm}
            onChange={setSearchTerm}
            onSearch={handleSearch}
            onReset={handleSearchReset}
            placeholder="Search Speaker Name"
            showFilter={false}
          />
          <Select
            value={visibilityFilter}
            onChange={setVisibilityFilter}
            style={{ width: 200 }}
            size="small"
            options={[
              { label: 'Profile Visibility: All', value: 'all' },
              { label: 'Visible', value: 'visible' },
              { label: 'Hidden', value: 'hidden' }
            ]}
          />
        </div>
        <Button variant="primary" icon={<PlusCircleFilled />} onClick={handleCreateClick}>
          Add New {ROLE_LABELS[activeRole].singular}
        </Button>
      </div>

      <div className="mb-6">
        {roleOrder.length > 1 && (
          <p className="text-xs text-gray-400 mb-2">Drag tabs to reorder</p>
        )}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleRoleDragEnd}>
          <SortableContext items={roleOrder} strategy={horizontalListSortingStrategy}>
            <div className="inline-flex items-center gap-1 rounded-md bg-gray-100 p-1">
              {roleOrder.map(role => (
                <SortableRoleChip
                  key={role}
                  role={role}
                  label={ROLE_LABELS[role].plural}
                  active={role === activeRole}
                  onSelect={() => setActiveRole(role)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      {filteredSpeakers.length === 0 ? (
        <ZeroState
          icon={<UserOutlined className="text-6xl text-gray-300" />}
          title={`No ${ROLE_LABELS[activeRole].plural} Yet`}
          description={
            searchTerm || visibilityFilter !== 'all'
              ? `No ${ROLE_LABELS[activeRole].plural.toLowerCase()} match your filters`
              : `Add ${ROLE_LABELS[activeRole].plural.toLowerCase()} to feature in your event agenda`
          }
        />
      ) : isReorderEnabled ? (
        <>
          <p className="text-xs text-gray-400 mb-3">Drag cards to reorder</p>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={filteredSpeakers.map(s => s.id)} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {filteredSpeakers.map(speaker => (
                  <SortableSpeakerCard
                    key={speaker.id}
                    speaker={speaker}
                    onEdit={handleEditClick}
                    onDelete={handleDeleteClick}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {filteredSpeakers.map(speaker => (
            <SpeakerCard
              key={speaker.id}
              speaker={speaker}
              onEdit={handleEditClick}
              onDelete={handleDeleteClick}
            />
          ))}
        </div>
      )}

      <SpeakerDrawer
        event={event}
        speaker={editingSpeaker}
        defaultRole={activeRole}
        open={drawerVisible}
        onClose={() => {
          setDrawerVisible(false)
          setEditingSpeaker(null)
        }}
      />

      <ConfirmModal
        open={deleteModal.visible}
        title="Delete speaker"
        onConfirm={handleDelete}
        onClose={() => setDeleteModal({ visible: false, speakerId: null, speakerName: '' })}
        confirmText="Yes"
        danger
      >
        Are you sure you want to delete this speaker?
      </ConfirmModal>
    </div>
  )
}
